/**
 * screenCmsKeys.js
 *
 * Ordered list of every onboarding route and the CMS keys that screen uses.
 * OnboardingLayout reads this to silently prefetch the NEXT screen's CMS
 * while the user is still on the current screen, so navigation is instant.
 *
 * Rules:
 *   - Order must match the real navigation flow.
 *   - If a route has dynamic segments (e.g. /OB18/:slug), list only the
 *     prefix — the matching logic uses startsWith for those entries.
 *   - Include modal CMS keys in the same entry as the screen that opens them
 *     (e.g. StripeCardModal keys live inside the id-verify entry).
 */

import { STEP_TO_PATH } from '../context/OnboardingContext';

export const SCREEN_SEQUENCE = [
  // ── 1 + 2: Landing form + OTP (same route) ──────────────────────────────
  {
    route: '/onboarding/OB1',
    keys: [
      'onboarding_landing_title',
      'onboarding_landing_subtitle',
      // VerifyPopup (state 1 = form, state 2 = OTP)
      'onboarding_verify_popup_state1_title',
      'onboarding_verify_popup_state1_subtitle',
      'onboarding_verify_popup_field_name_label',
      'onboarding_verify_popup_field_phone_label',
      'onboarding_verify_popup_field_email_label',
      'onboarding_verify_popup_state1_cta',
      'onboarding_verify_popup_state2_title',
      'onboarding_verify_popup_state2_subtitle',
      'onboarding_verify_popup_state2_cta',
      'onboarding_verify_popup_resend',
      // Chat panel keys shared across all screens
      'onboarding_chat_panel_title',
      'onboarding_chat_panel_subtitle',
      'onboarding_chat_panel_autosaved_label',
      'onboarding_chat_panel_input_placeholder',
    ],
  },

  // ── 3: Qualify ───────────────────────────────────────────────────────────
  {
    route: STEP_TO_PATH['qualify'],
    keys: [
      'onboarding_qualify_eyebrow',
      'onboarding_qualify_title',
      'onboarding_qualify_q1_label',
      'onboarding_qualify_q2_label',
      'onboarding_qualify_q3_label',
      'onboarding_qualify_q4_label',
      'onboarding_qualify_q5_label',
      'onboarding_qualify_q3_sublabel',
      'onboarding_qualify_q4_sublabel',
      'onboarding_qualify_q5_sublabel',
      'onboarding_qualify_cta',
      'onboarding_qualify_chat_bot_greeting',
      'onboarding_qualify_pms_options',
      'onboarding_qualify_protection_options',
      'onboarding_qualify_feature_options',
      'onboarding_qualify_referral_options',
    ],
  },

  // ── 4: Demo Tour ─────────────────────────────────────────────────────────
  {
    route: STEP_TO_PATH['demo-tour'],
    keys: [
      'onboarding_demo_eyebrow',
      'onboarding_demo_title',
      'onboarding_demo_body',
      'onboarding_demo_play_cta',
      'onboarding_demo_cta',
      'onboarding_demo_chat_bot_greeting',
      'onboarding_demo_tour',
      'onboarding_demo_tour_journey_steps',
      'onboarding_demo_tour_reservation_confirmed',
      'onboarding_demo_tour_placeholder_name',
    ],
  },

  // ── 5: Let's Go ──────────────────────────────────────────────────────────
  {
    route: STEP_TO_PATH['lets-go'],
    keys: [
      'onboarding_lets_go_title_1',
      'onboarding_lets_go_title_2',
      'onboarding_lets_go_body',
      'onboarding_lets_go_cta',
      'onboarding_lets_go_emoji'
    ],
  },

  // ── 6: Connect PMS (+ credential modal) ─────────────────────────────────
  {
    route: STEP_TO_PATH['connect-pms'],
    keys: [
      'onboarding_connect_pms_step_number',
      'onboarding_connect_pms_eyebrow',
      'onboarding_connect_pms_heading',
      'onboarding_connect_pms_body',
      'onboarding_connect_pms_subheading',
      'onboarding_connect_pms_grid_label',
      'onboarding_connect_pms_grid_hint',
      'onboarding_connect_pms_grid_error',
      'onboarding_connect_pms_modal_subtitle',
      'onboarding_connect_pms_modal_submit',
      'onboarding_connect_pms_select_label',
      'onboarding_connect_pms_select_placeholder',
      'onboarding_connect_pms_oauth_cta',
      'onboarding_connect_pms_oauth_helper',
      'onboarding_connect_pms_credential_cta',
      'onboarding_connect_pms_credential_helper',
      'onboarding_connect_pms_skip_cta',
      'onboarding_connect_pms_skip_helper',
      'onboarding_connect_pms_error_message',
      'onboarding_connect_pms_checking',
      'onboarding_connect_pms_progress_status',
      'onboarding_connect_pms_progress_sample1',
      'onboarding_connect_pms_progress_sample2',
      'onboarding_connect_pms_progress_sample3',
      'onboarding_connect_pms_progress_sample4',
      'onboarding_cms_pms_list',
    ],
  },

  // ── 7: Plan ──────────────────────────────────────────────────────────────
  {
    route: STEP_TO_PATH['plan'],
    keys: [
      'onboarding_plan_eyebrow',
      'onboarding_plan_title',
      'onboarding_plan_body',
      'onboarding_plan_cta',
      'onboarding_plan_module1_section',
      'onboarding_plan_module1_description',
      'onboarding_plan_module1_badge',
      'onboarding_plan_other_section',
      'onboarding_plan_module2_description',
      'onboarding_plan_module3_description',
      'onboarding_plan_module4_description',
      'onboarding_plan_module_later_badge',
      'onboarding_plan_chat_bot_greeting',
    ],
  },

  // ── 8: Guest Registration ────────────────────────────────────────────────
  {
    route: STEP_TO_PATH['guest-registration'],
    keys: [
      'onboarding_guest_reg_eyebrow',
      'onboarding_guest_reg_title',
      'onboarding_guest_reg_body',
      'onboarding_guest_reg_cta',
      'onboarding_guest_reg_chat_bot_greeting',
      'onboarding_guest_registration_flow_items',
    ],
  },

  // ── 9: Customize ─────────────────────────────────────────────────────────
  {
    route: STEP_TO_PATH['customize'],
    keys: [
      'onboarding_customize_subtitle',
      'onboarding_customize_title',
      'onboarding_customize_body',
      'onboarding_customize_field_name_label',
      'onboarding_customize_field_logo_label',
      'onboarding_customize_field_bg_label',
      'onboarding_customize_cta',
      'onboarding_customize_chat_questions',
      'onboarding_customize_preview_property_name',
      'onboarding_customize_preview_welcome',
      'onboarding_customize_preview_tagline',
      'onboarding_customize_preview_steps_label',
      'onboarding_customize_preview_avatar_initials',
      'onboarding_customize_preview_cta',
      'onboarding_customize_chat_bot_greeting_intro',
      'onboarding_customize_chat_bot_greeting_followup',
    ],
  },

  // ── 10: Sign ─────────────────────────────────────────────────────────────
  {
    route: STEP_TO_PATH['sign'],
    keys: [
      'onboarding_beat_agree_subtitle',
      'onboarding_beat_agree_title',
      'onboarding_beat_agree_body',
      'onboarding_beat_agree_cta_primary',
      'onboarding_beat_agree_cta_ghost',
      'onboarding_beat_agree_preview_rules_title',
      'onboarding_beat_agree_preview_agreement_title',
      'onboarding_beat_agree_chat_questions',
      'onboarding_beat_agree_callout1_text',
      'onboarding_beat_agree_callout2_text',
      'onboarding_beat_agree_chat_bot_greeting',
      'onboarding_sign_here_label',
    ],
  },

  // ── 11: ID Verify (+ Stripe modal that opens from this screen) ───────────
  {
    route: STEP_TO_PATH['id-verify'],
    keys: [
      'onboarding_beat_ids_subtitle',
      'onboarding_beat_ids_title',
      'onboarding_beat_ids_body',
      'onboarding_beat_ids_cta',
      'onboarding_beat_ids_chat_questions',
      'onboarding_beat_ids_chat_bot_greeting',
      'onboarding_id_verify_plans',
      // StripeCardModal — opens from this screen
      'onboarding_stripe_modal_title',
      'onboarding_stripe_modal_badge',
      'onboarding_stripe_modal_price',
      'onboarding_stripe_modal_nothing_charged',
      'onboarding_stripe_modal_subtitle',
      'onboarding_stripe_modal_cta',
    ],
  },

  // ── 12: Damage ───────────────────────────────────────────────────────────
  {
    route: STEP_TO_PATH['damage'],
    keys: [
      'onboarding_damage_eyebrow',
      'onboarding_damage_title',
      'onboarding_damage_body',
      'onboarding_damage_optionA_title',
      'onboarding_damage_optionA_badge',
      'onboarding_damage_optionA_description',
      'onboarding_damage_optionB_title',
      'onboarding_damage_optionB_badge',
      'onboarding_damage_optionB_description',
      'onboarding_damage_cta',
      'onboarding_damage_chat_questions',
      'onboarding_damage_chat_bot_greeting',
    ],
  },

  // ── 13: Coverage ─────────────────────────────────────────────────────────
  {
    route: STEP_TO_PATH['coverage'],
    keys: [
      'onboarding_coverage_tiers_subtitle',
      'onboarding_coverage_tiers_title',
      'onboarding_coverage_tiers_cta_primary',
      'onboarding_coverage_tiers_cta_ghost',
      'onboarding_coverage_tiers_chat_questions',
      'onboarding_finish_with_human_href',
      'onboarding_coverage_tiers_chat_bot_greeting',
      'onboarding_coverage_ranks',
      'onboarding_coverage_rank_to_slug',
      'onboarding_coverage_pms_confirming',
      'onboarding_coverage_pms_timeout',
      'onboarding_coverage_pms_error',
      'onboarding_coverage_tiers_features_starter',
      'onboarding_coverage_tiers_features_pro',
      'onboarding_coverage_tiers_features_premier',
    ],
  },

  // ── 14: Social Proof ─────────────────────────────────────────────────────
  // Includes booking-apply keys here because social-proof navigates directly
  // to booking-apply (skipping proof-stats), so OnboardingLayout prefetches both.
  {
    route: STEP_TO_PATH['social-proof'],
    keys: [
      'onboarding_social_proof_eyebrow',
      'onboarding_social_proof_title',
      'onboarding_social_proof_cta',
      'onboarding_social_proof_chat_bot',
      'onboarding_social_proof_chat_questions',
      'onboarding_social_proof_gregory_quote',
      'onboarding_social_proof_gregory_attribution',
      'onboarding_social_proof_stats',
      // booking-apply (next screen after both social-proof and proof-stats)
      'onboarding_booking_apply_eyebrow',
      'onboarding_booking_apply_title',
      'onboarding_booking_apply_body',
      'onboarding_booking_apply_optionA_title',
      'onboarding_booking_apply_optionA_description',
      'onboarding_booking_apply_optionB_title',
      'onboarding_booking_apply_optionB_badge',
      'onboarding_booking_apply_optionB_description',
      'onboarding_booking_apply_cta',
      'onboarding_booking_apply_chat_questions',
      'onboarding_booking_apply_chat_bot_greeting',
    ],
  },

  // ── 14b: Proof Stats (follows social-proof) ───────────────────────────────
  {
    route: STEP_TO_PATH['proof-stats'],
    keys: [
      'onboarding_social_proof_eyebrow',
      'onboarding_social_proof_title',
      'onboarding_social_proof_cta',
      'onboarding_social_proof_chat_bot',
      'onboarding_social_proof_chat_questions',
      'onboarding_social_proof_stephen_quote',
      'onboarding_social_proof_stephen_quote_cons',
      'onboarding_social_proof_stephen_attribution',
      'onboarding_social_proof_capterra_label',
      'onboarding_social_proof_stats',
      // booking-apply (next screen after both social-proof and proof-stats)
      'onboarding_booking_apply_eyebrow',
      'onboarding_booking_apply_title',
      'onboarding_booking_apply_body',
      'onboarding_booking_apply_optionA_title',
      'onboarding_booking_apply_optionA_description',
      'onboarding_booking_apply_optionB_title',
      'onboarding_booking_apply_optionB_badge',
      'onboarding_booking_apply_optionB_description',
      'onboarding_booking_apply_cta',
      'onboarding_booking_apply_chat_questions',
      'onboarding_booking_apply_chat_bot_greeting',
    ],
  },

  // ── 15: Booking Apply ────────────────────────────────────────────────────
  {
    route: STEP_TO_PATH['booking-apply'],
    keys: [
      'onboarding_booking_apply_eyebrow',
      'onboarding_booking_apply_title',
      'onboarding_booking_apply_body',
      'onboarding_booking_apply_optionA_title',
      'onboarding_booking_apply_optionA_description',
      'onboarding_booking_apply_optionB_title',
      'onboarding_booking_apply_optionB_badge',
      'onboarding_booking_apply_optionB_description',
      'onboarding_booking_apply_cta',
      'onboarding_booking_apply_chat_questions',
      'onboarding_booking_apply_chat_bot_greeting',
    ],
  },

  // ── 16: Guidebooks Reveal ────────────────────────────────────────────────
  {
    route: STEP_TO_PATH['guidebooks-reveal'],
    keys: [
      'onboarding_guidebooks_reveal_emoji',
      'onboarding_guidebooks_reveal_headline',
      'onboarding_guidebooks_reveal_subtitle',
      'onboarding_guidebooks_reveal_cta',
      'onboarding_guidebooks_reveal_pms_hint',
    ],
  },

  // ── 16b: Arrival Choice (between guidebooks-reveal and guidebook) ─────────
  {
    route: STEP_TO_PATH['arrival-choice'],
    keys: [
      'onboarding_arrival_choice',
      'onboarding_arrival_confirmation',
    ],
  },

  // ── 16c: Arrival Guidebook Portal ───────────────────────────────────────
  {
    route: STEP_TO_PATH['arrival-confirmation'],
    keys: [
      'onboarding_arrival_confirmation',
    ],
  },

  // ── 17: Guidebook /slug (dynamic — prefix match) ─────────────────────────
  {
    route: STEP_TO_PATH['guidebook'],
    keys: [
      'onboarding_review_guidebook_subtitle_template',
      'onboarding_review_guidebook_title',
      'onboarding_review_guidebook_body_template',
      'onboarding_review_guidebook_action_edit',
      'onboarding_review_guidebook_action_photos',
      'onboarding_review_guidebook_cta_next',
      'onboarding_review_guidebook_cta_last',
      'onboarding_review_guidebook_section_getting_in',
      'onboarding_review_guidebook_section_local_faves',
      'onboarding_review_guidebook_section_wifi_home',
      'onboarding_review_guidebook_theme_og_label',
      'onboarding_review_guidebook_theme_og_desc',
      'onboarding_review_guidebook_theme_sunset_label',
      'onboarding_review_guidebook_theme_sunset_desc',
      'onboarding_review_guidebook_theme_desert_label',
      'onboarding_review_guidebook_theme_desert_desc',
      'onboarding_review_guidebook_chat_bot_greeting',
      'onboarding_review_guidebook_guide_label',
      'onboarding_review_guidebook_tagline',
      'onboarding_review_guidebook_loading',
      'onboarding_review_guidebook_no_content',
      'onboarding_review_guidebook_no_properties_title',
      'onboarding_review_guidebook_no_properties_body',
      'onboarding_review_guidebook_reconnect_btn',
      'onboarding_review_guidebook_arrival_label',
      'onboarding_guest_reg_confirmation',
      'onboarding_review_guidebook_eyebrow'
    ],
  },

  // ── 17b: Guest Registration Overview ────────────────────────────────────
  {
    route: STEP_TO_PATH['guest-registration-overview'],
    keys: [
      'onboarding_guest_reg_confirmation',
      'onboarding_guest_reg_verification',
    ],
  },

  // ── 17c: Guest Registration Confirmation ───────────────────────────────
  {
    route: STEP_TO_PATH['guest-registration-confirmation'],
    keys: [
      'onboarding_guest_reg_verification',
      'onboarding_ota_compliance_intro',
      'onboarding_stripe_modal_title',
      'onboarding_stripe_modal_badge',
      'onboarding_stripe_modal_price',
      'onboarding_stripe_modal_nothing_charged',
      'onboarding_stripe_modal_subtitle',
      'onboarding_stripe_modal_cta',
    ],
  },

  // ── 17d: Task Intro ────────────────────────────────────────────────────
  {
    route: STEP_TO_PATH['task-intro'],
    keys: [
      'onboarding_ota_compliance_intro',
      'onboarding_task_detail',
    ],
  },

  // ── 17e: Task Detail ──────────────────────────────────────────────────
  {
    route: STEP_TO_PATH['task-detail'],
    keys: [
      'onboarding_task_detail',
    ],
  },

  // ── 18: Messaging Intro ──────────────────────────────────────────────────
  {
    route: STEP_TO_PATH['messaging-intro'],
    keys: [
      'onboarding_messages_setup_intro',
    ],
  },

  // ── 19: Messaging Templates ──────────────────────────────────────────────
  {
    route: STEP_TO_PATH['messaging-templates'],
    keys: [
      'onboarding_message_templates_intro',
      'onboarding_message_editor_v2_warning_title',
      'onboarding_message_editor_v2_warning_line1',
      'onboarding_message_editor_v2_warning_line2',
      'onboarding_message_editor_v2_cta_done',
      'onboarding_message_editor_v2_sends_label',
    ],
  },

  // ── 20: SMS Reveal ───────────────────────────────────────────────────────
  {
    route: STEP_TO_PATH['sms-reveal'],
    keys: [
      'onboarding_sms_reveal_emoji',
      'onboarding_sms_reveal_headline',
      'onboarding_sms_reveal_subtitle',
      'onboarding_sms_reveal_cta',
      'onboarding_sms_reveal_title',
      'onboarding_sms_reveal_body',
    ],
  },

  // ── 21: Disable PMS Messages ─────────────────────────────────────────────
  {
    route: STEP_TO_PATH['disable-pms-messages'],
    keys: [
      'onboarding_auto_cleanup_eyebrow',
      'onboarding_auto_cleanup_title',
      'onboarding_auto_cleanup_body',
      'onboarding_auto_cleanup_step1',
      'onboarding_auto_cleanup_step2',
      'onboarding_auto_cleanup_step3',
      'onboarding_auto_cleanup_step4',
      'onboarding_auto_cleanup_cta_primary',
      'onboarding_auto_cleanup_chat_bot_greeting',
      'onboarding_auto_cleanup_steps_hospitable',
      'onboarding_auto_cleanup_steps_lodgify',
      'onboarding_auto_cleanup_steps_guesty',
      'onboarding_auto_cleanup_steps_hostaway',
      'onboarding_auto_cleanup_steps_ownerrez',
      'onboarding_auto_cleanup_steps_cloudbeds',
      'onboarding_auto_cleanup_steps_streamline',
      'onboarding_auto_cleanup_steps_track',
    ],
  },

  // ── 22: Complete ─────────────────────────────────────────────────────────
  {
    route: STEP_TO_PATH['complete'],
    keys: [
      'onboarding_complete_emoji',
      'onboarding_complete_title',
      'onboarding_complete_subtitle',
      'onboarding_complete_body',
      'onboarding_complete_cta',
    ],
  },
];

/**
 * Find the index of the current pathname in SCREEN_SEQUENCE.
 * Uses exact match first, then prefix match for dynamic routes like
 * /onboarding/OB18/:slug.
 */
export const findScreenIndex = (pathname) => {
  const exactIdx = SCREEN_SEQUENCE.findIndex((s) => s.route === pathname);
  if (exactIdx !== -1) return exactIdx;
  return SCREEN_SEQUENCE.findIndex((s) => pathname.startsWith(s.route + '/'));
};
